/**
 * AudioContext lifecycle and the bus topology.
 *
 * THE HARNESS CONTRACT (read this before changing anything here):
 * WebAudio needs a user gesture. The headless harness never produces one, so this class is
 * built so that if the context never starts, NOTHING in the audio subsystem ever touches a
 * WebAudio API. `live` stays false, every voice class short-circuits, and the shots capture
 * exactly as if the subsystem were not there. There is no try/catch soup: the context
 * simply does not exist.
 *
 * Topology:
 *
 *     engine ┐
 *     tyres  ├─→ bedBus ──→ duckGain ┐
 *     music  │                        ├─→ master ─→ toneLpf ─→ limiter ─→ destination
 *     amb    ┘                        │
 *     sfx  ───────────────────────────┤
 *     voice ──────────────────────────┘
 *
 * Beds duck under the announcer; sfx and the announcer itself never duck, because an arcade
 * bark that swallows its own hit sound is worse than one that fights the music.
 */

import { AUDIO_TUNING as T } from './tuning.js';
import { toAudioBuffers } from './banks.js';

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousedown'];

export class AudioGraph {
  constructor(opts = {}) {
    /** True only once a real AudioContext exists and is running. */
    this.live = false;
    /** True once we have attempted a start and it failed — stops us retrying every frame. */
    this.failed = false;
    /** Hard disable. Set by the harness, ?mute=1, or a construction option. */
    this.disabled = !!opts.disabled;
    this.actx = null;
    this.buffers = null;
    this.baked = opts.baked || null;
    this.onStart = opts.onStart || (() => {});
    /**
     * Optional pre-built context. The offline verification rig (tools/audio-verify.mjs)
     * passes an OfflineAudioContext here so the exact same graph can be rendered to PCM and
     * measured with an FFT — which is the only way to prove a soundscape is correct without
     * a pair of ears. The game never sets this.
     */
    this.injectedContext = opts.context || null;
    this._gestureBound = false;
    this._gestureHandler = null;
    this._duckUntil = 0;
    this._muted = false;
    this.startAttempts = 0;
  }

  /**
   * Attach one-shot listeners so the context starts on the player's first input. Safe to
   * call when disabled — it simply does nothing, so the harness never binds a listener.
   */
  armGesture(target = (typeof window !== 'undefined' ? window : null)) {
    if (this.disabled || this._gestureBound || !target || this.live) return;
    this._gestureBound = true;
    this._gestureTarget = target;
    this._gestureHandler = () => { this.start(); };
    for (const e of GESTURE_EVENTS) {
      target.addEventListener(e, this._gestureHandler, { passive: true });
    }
  }

  _disarmGesture() {
    if (!this._gestureBound) return;
    for (const e of GESTURE_EVENTS) {
      this._gestureTarget.removeEventListener(e, this._gestureHandler);
    }
    this._gestureBound = false;
    this._gestureHandler = null;
  }

  /**
   * Create the context and build the whole graph. Idempotent. Returns true if live.
   * Every node in the persistent graph is created exactly once, here.
   */
  start() {
    if (this.live) return true;
    if (this.disabled || this.failed) return false;
    this.startAttempts++;

    let actx = this.injectedContext;
    if (!actx) {
      if (typeof window === 'undefined') return false;
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) { this.failed = true; return false; }
      try {
        actx = new Ctor({ latencyHint: 'interactive' });
      } catch {
        this.failed = true;
        return false;
      }
      // Some browsers hand back a suspended context even from inside a gesture.
      if (actx.state === 'suspended' && actx.resume) actx.resume().catch(() => {});
    }
    this.actx = actx;

    const now = actx.currentTime;

    // ---- master chain
    const limiter = actx.createDynamicsCompressor();
    limiter.threshold.value = T.master.limiter.threshold;
    limiter.knee.value = T.master.limiter.knee;
    limiter.ratio.value = T.master.limiter.ratio;
    limiter.attack.value = T.master.limiter.attack;
    limiter.release.value = T.master.limiter.release;
    limiter.connect(actx.destination);

    const toneLpf = actx.createBiquadFilter();
    toneLpf.type = 'lowpass';
    toneLpf.frequency.value = T.master.toneLpfHz;
    toneLpf.Q.value = T.master.toneLpfQ;
    toneLpf.connect(limiter);

    const toneHpf = actx.createBiquadFilter();
    toneHpf.type = 'highpass';
    toneHpf.frequency.value = T.master.toneHpHz;
    toneHpf.Q.value = T.master.toneHpQ;
    toneHpf.connect(toneLpf);

    const master = actx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(T.master.gain, now + T.master.bootFade);
    master.connect(toneHpf);

    // ---- duckable bed
    const duckGain = actx.createGain();
    duckGain.gain.value = 1;
    duckGain.connect(master);

    // Each bus is optionally followed by a highpass (see AUDIO_TUNING.bus.hp).
    const mk = (name, dest) => {
      const g = actx.createGain();
      g.gain.value = T.bus[name];
      const hp = T.bus.hp[name];
      if (hp) {
        const f = actx.createBiquadFilter();
        f.type = 'highpass';
        f.frequency.value = hp;
        f.Q.value = 0.7;
        g.connect(f);
        f.connect(dest);
      } else {
        g.connect(dest);
      }
      return g;
    };

    const bus = {
      engine: mk('engine', duckGain),
      tyres: mk('tyres', duckGain),
      music: mk('music', duckGain),
      ambience: mk('ambience', duckGain),
      sfx: mk('sfx', master),
      voice: mk('voice', master),
    };

    this.limiter = limiter;
    this.toneLpf = toneLpf;
    this.toneHpf = toneHpf;
    this.master = master;
    this.duckGain = duckGain;
    this.bus = bus;

    this.buffers = this.baked ? toAudioBuffers(actx, this.baked) : null;

    this.live = true;
    this._disarmGesture();
    try { this.onStart(this); } catch (e) { console.warn('[audio] onStart', e); }
    return true;
  }

  /** Current audio clock. Zero when not live, which is safe for every caller here. */
  get now() { return this.live ? this.actx.currentTime : 0; }

  /** Sample rate of the live context, or the bake rate when there is none. */
  get sampleRate() { return this.live ? this.actx.sampleRate : T.renderRate; }

  /** Fetch a baked AudioBuffer by name. Null when not live. */
  buffer(name) { return this.buffers ? this.buffers.oneShots.get(name) : null; }
  get whiteBuffer() { return this.buffers ? this.buffers.white : null; }
  get pinkBuffer() { return this.buffers ? this.buffers.pink : null; }

  /**
   * Duck the bed. `amount` is the fraction removed (0.4 → bed drops to 0.6).
   * Overlapping ducks take the deepest, and the release always ramps back to unity.
   */
  duck(seconds, amount) {
    if (!this.live) return;
    const g = this.duckGain.gain;
    const now = this.actx.currentTime;
    const target = Math.max(0.02, 1 - Math.min(0.95, amount));
    const holdUntil = now + Math.max(0.02, seconds);
    if (holdUntil <= this._duckUntil && target >= (this._duckTarget || 1)) return;
    this._duckUntil = Math.max(this._duckUntil, holdUntil);
    this._duckTarget = target;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(target, now + T.duck.voiceAttack);
    g.setValueAtTime(target, this._duckUntil);
    g.linearRampToValueAtTime(1, this._duckUntil + T.duck.voiceRelease);
  }

  /** Mute without tearing the graph down — used by freezeClocks() and the pause screen. */
  setMuted(m) {
    this._muted = m;
    if (!this.live) return;
    const now = this.actx.currentTime;
    const g = this.master.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(0.0001, g.value), now);
    g.linearRampToValueAtTime(m ? 0.0001 : T.master.gain, now + 0.08);
  }

  get muted() { return this._muted; }

  /**
   * A persistent looping noise source. Started once, never stopped. Every noise layer in
   * the game taps one of these instead of creating a source per frame.
   */
  makeNoiseSource(kind = 'white') {
    if (!this.live) return null;
    const src = this.actx.createBufferSource();
    src.buffer = kind === 'pink' ? this.pinkBuffer : this.whiteBuffer;
    src.loop = true;
    // A small playbackRate offset per source de-correlates layers that share a buffer,
    // which is what stops two noise beds phasing into an audible flanger.
    src.start(0);
    return src;
  }

  param(p, value, glide) {
    if (!this.live) return;
    const now = this.actx.currentTime;
    p.setTargetAtTime(value, now, Math.max(0.001, glide));
  }

  async suspend() { if (this.live && this.actx.state === 'running') { try { await this.actx.suspend(); } catch { /* ignore */ } } }
  async resume() { if (this.live && this.actx.state !== 'running') { try { await this.actx.resume(); } catch { /* ignore */ } } }

  dispose() {
    this._disarmGesture();
    if (this.actx) { try { this.actx.close(); } catch { /* ignore */ } }
    this.actx = null;
    this.live = false;
    this.buffers = null;
  }
}
